import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type Hls } from '@/scripts/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { percentageSeen } from '@/scripts/theme/animations'
import { type ArtDirection } from '@/scripts/core/art-direction'

function isVideoPlaying(video: HTMLVideoElement): boolean {
	return !video.paused && !video.ended && video.currentTime > 0
}

export class UcoastVideo extends UcoastEl {
	static htmlSelector = 'ucoast-video'
	videoEl: HTMLVideoElement
	hlsReady: boolean
	hlsSource?: string
	mp4Source?: string
	initialized = false
	usingMp4Source: boolean
	hlsInstance: Hls
	eventDriven: boolean
	eventEnabled: boolean
	wrapper?: ArtDirection
	userPaused = false
	userTouchedMute = false
	playPauseButton?: HTMLButtonElement
	muteButton?: HTMLButtonElement
	private playPauseSrOnly?: HTMLElement
	private muteSrOnly?: HTMLElement
	private boundHandlePlayPauseClick = () => {
		void this.togglePlayPause()
	}
	private boundHandleMuteClick = () => {
		this.toggleMute()
	}
	private boundSyncPlayingState = () => this.syncPlayingState()
	private boundSyncMutedState = () => this.syncMutedState()

	constructor() {
		super()
		this.videoEl = q.rs<HTMLVideoElement>('video', this)
		this.hlsSource = this.videoEl.getAttribute('data-hls-src') ?? undefined
		this.hlsReady = false
		this.mp4Source = this.videoEl.getAttribute('data-mp4-src') ?? undefined
		this.usingMp4Source = false
		this.initialized = true
		this.eventDriven = this.hasAttribute('data-event-driven')
		this.eventEnabled = false
		this.wrapper = q.oc(this, 'art-direction')
		this.playPauseButton = q.os<HTMLButtonElement>(
			'[data-uc-control="play-pause"]',
			this
		)
		this.muteButton = q.os<HTMLButtonElement>(
			'[data-uc-control="mute"]',
			this
		)
		this.playPauseSrOnly = this.playPauseButton
			? q.os<HTMLElement>('.visually-hidden', this.playPauseButton)
			: undefined
		this.muteSrOnly = this.muteButton
			? q.os<HTMLElement>('.visually-hidden', this.muteButton)
			: undefined
	}

	override async connectedCallback() {
		super.connectedCallback()
		this.onConnectedCallback()
		this.bindControls()
		this.syncMutedState()
		this.syncPlayingState()
	}

	bindControls() {
		this.playPauseButton?.addEventListener('click', this.boundHandlePlayPauseClick)
		this.muteButton?.addEventListener('click', this.boundHandleMuteClick)
		this.videoEl.addEventListener('play', this.boundSyncPlayingState)
		this.videoEl.addEventListener('pause', this.boundSyncPlayingState)
		this.videoEl.addEventListener('volumechange', this.boundSyncMutedState)
	}

	unbindControls() {
		this.playPauseButton?.removeEventListener('click', this.boundHandlePlayPauseClick)
		this.muteButton?.removeEventListener('click', this.boundHandleMuteClick)
		this.videoEl.removeEventListener('play', this.boundSyncPlayingState)
		this.videoEl.removeEventListener('pause', this.boundSyncPlayingState)
		this.videoEl.removeEventListener('volumechange', this.boundSyncMutedState)
	}

	async togglePlayPause() {
		if (!this.videoEl.paused) {
			this.userPaused = true
			this.pause()
			return
		}
		this.userPaused = false
		// autoplay defaults to muted; manual play implies user wants sound,
		// unless they've explicitly toggled mute
		if (!this.userTouchedMute && this.videoEl.muted) {
			this.videoEl.muted = false
			this.syncMutedState()
		}
		await this.forcePlay()
	}

	async forcePlay() {
		if (this.eventDriven) this.eventEnabled = true
		await this.executePlay('forcePlay')
	}

	// shared play path used by both `play()` (with viewport/event guards) and
	// `forcePlay()` (no guards — user-initiated)
	private async executePlay(context: 'play' | 'forcePlay') {
		if (!this.hlsSource && !this.usingMp4Source && this.mp4Source) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		try {
			await this.videoEl.play()
			this.removeAttribute('data-uc-autoplay-blocked')
			this.setAttribute('data-uc-has-played', 'true')
			this.wrapper?.onMediaLoad()
		} catch (err) {
			this.setAttribute('data-uc-autoplay-blocked', 'true')
			console.warn(`ucoast-video: ${context} failed`, err)
		}
	}

	toggleMute() {
		this.userTouchedMute = true
		this.videoEl.muted = !this.videoEl.muted
		this.syncMutedState()
	}

	syncPlayingState() {
		// `play` event can fire before currentTime > 0, so don't use isVideoPlaying here
		const playing = !this.videoEl.paused && !this.videoEl.ended
		this.setAttribute('data-uc-playing', String(playing))
		if (!this.playPauseButton) return
		this.playPauseButton.setAttribute(
			'data-state',
			playing ? 'playing' : 'paused'
		)
		this.updateButtonLabel(
			this.playPauseButton,
			this.playPauseSrOnly,
			playing ? 'pause' : 'play'
		)
	}

	syncMutedState() {
		const muted = this.videoEl.muted
		this.setAttribute('data-uc-muted', String(muted))
		if (!this.muteButton) return
		this.muteButton.setAttribute('data-state', muted ? 'muted' : 'unmuted')
		this.updateButtonLabel(
			this.muteButton,
			this.muteSrOnly,
			muted ? 'unmute' : 'mute'
		)
	}

	private updateButtonLabel(
		button: HTMLButtonElement,
		srOnly: HTMLElement | undefined,
		labelKey: string
	) {
		const ariaLabel = button.dataset[labelKey + 'Label'] ?? null
		if (!ariaLabel) return
		button.setAttribute('aria-label', ariaLabel)
		if (srOnly) srOnly.textContent = ariaLabel
	}

	onConnectedCallback() {
		if (!window?.Ucoast?.mediaManager) {
			this.markForRetry()
		}
		if (window?.Ucoast?.mediaManager?.hlsLibraryLoaded) {
			void this.onLibraryLoad()
		} else if (!this.hlsSource && this.mp4Source) {
			window.Ucoast.mediaManager.addVideo(this)
		} else if (
			this.hlsSource &&
			window.Ucoast?.mediaManager?.hlsRequired !== undefined &&
			window.Ucoast.mediaManager.hlsRequired === false
		) {
			window.Ucoast.mediaManager.addVideo(this)
		}
	}

	markForRetry() {
		this.setAttribute('data-retry', 'true')
	}

	// used by mediaManager to retry initialization
	unMarkForRetry() {
		this.removeAttribute('data-retry')
	}

	override async disconnectedCallback() {
		super.disconnectedCallback()
		this.unbindControls()
		if (this.hlsInstance) {
			this.hlsInstance.destroy()
			window.Ucoast.mediaManager.removeVideo(this)
		}
	}

	// both playEvent methods are used by remote components
	async playEventOn() {
		if (!this.eventDriven) return
		this.eventEnabled = true
		await this.play()
	}

	playEventOff() {
		this.eventDriven = true // sometimes we need to convert auto-play to event-driven
		this.eventEnabled = false
		this.pause()
	}

	isTouchingViewport() {
		const rect = this.getBoundingClientRect()
		return (
			rect.top <= window.innerHeight &&
			rect.bottom >= 0 &&
			rect.left <= window.innerWidth &&
			rect.right >= 0
		)
	}

	async play() {
		// all conditions that should prevent the video from playing must go here
		if (this.userPaused) return
		if (!this.isTouchingViewport()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (this.isHiddenForViewport()) return
		if (isVideoPlaying(this.videoEl)) return
		if (this.isWaitingForHlsLibrary()) return
		if (percentageSeen(this.videoEl) <= 0) return

		// play video w/native html5 video element. play() can reject for autoplay
		// reasons we can't predict — most commonly iOS Safari Low Power Mode, but
		// also browser autoplay policies, missing user activation, etc. when that
		// happens, the underlying poster <img> rendered by art-direction.liquid
		// stays visible (the <video>'s own poster is a 1x1 transparent gif), so
		// the user sees the still image and can tap the play button if controls
		// are enabled. record the failure so consumers can react.
		await this.executePlay('play')
	}

	pause() {
		if (isVideoPlaying(this.videoEl)) {
			this.videoEl.pause()
		}
	}

	isWaitingForHlsLibrary() {
		if (!this.hlsSource) return false
		if (!window.Ucoast.mediaManager.hlsRequired) return false
		if (!this.hlsReady) return true
		return !window.Ucoast.mediaManager.hlsLibraryLoaded
	}

	async onLibraryLoad() {
		// don't run if this isn't an hls video or if hls is already initalized for this element
		if (!this.hlsSource) return
		if (this.hlsInstance) return
		// initialize hls - this is a 3ms process that doesn't load any video data until a video is played
		this.hlsInstance = new window.Ucoast.mediaManager.Hls.default()
		this.hlsInstance.on('hlsManifestParsed', () => {
			this.hlsReady = true
			void this.play()
		})
		this.videoEl.src = this.hlsSource
		await this.hlsInstance.loadSource(this.hlsSource)
		await this.hlsInstance.attachMedia(this.videoEl)
		// all videos present in DOM at page load are added to the mediaManager.videos array, but if this video was added to the DOM later, it needs to be added to the array

		window.Ucoast.mediaManager.addVideo(this)
	}

	isHiddenForViewport() {
		const rect = this.videoEl.getBoundingClientRect()
		return rect.height < 1 || rect.width < 1
	}
}
